import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import TransferPaymentForm from './TransferPaymentForm';
import { Paper, Box, Typography, CircularProgress, Chip, Divider, Grid } from '@mui/material';
import { Users, Lock } from 'lucide-react';

// Load Stripe outside of component render to avoid recreating it on every render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TransferPayment = ({ reviewData, onBack, createPayment, isLoading, onPaymentSuccess }) => {
  const [localLoading, setLocalLoading] = useState(false);
  
  // Combined loading state (either passed from parent or local)
  const loading = isLoading || localLoading;
  
  // Get number of lines from reviewData
  const numLines = reviewData?.userDetails?.numLines || reviewData?.planDetails?.lines || 1;
  
  // For transfers, we're just paying activation fee and shipping
  const activationFee = reviewData?.planDetails?.totalActivationFee || 35.00;
  const isEsim = reviewData?.userDetails?.hasEsim || false;
  const shippingCost = isEsim ? 0 : (reviewData?.subscriptionDetails?.shipping === 'expedited' ? 15 : 8);
  const totalAmount = Math.round((activationFee + shippingCost) * 100); // Convert to cents

  // Get plan details
  const carrierName = reviewData?.planDetails?.carrier || 'Verizon';
  const planName = reviewData?.planDetails?.planName || 'Unlimited Plan';
  const circleName = reviewData?.planDetails?.circleName || 'CircledIn';
  const displayName = `${circleName} - ${planName}`;
  
  // Monthly costs
  const monthlyRate = reviewData?.planDetails?.monthlyRate || 87.00;
  const taxes = reviewData?.planDetails?.taxes || 5.00;
  const totalMonthly = monthlyRate + taxes;

  // Initial options for Elements - Mirror your working component!
  const options = {
    // Use subscription mode like your working component
    mode: 'subscription',
    amount: totalAmount,
    currency: 'usd',
    // Don't set setup_future_usage explicitly
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#FF4775',
        colorBackground: '#ffffff',
        colorText: '#111827',
        colorDanger: '#EF4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '8px',
        spacingUnit: '4px',
      },
      rules: {
        '.Tab': {
          border: '1px solid #E5E7EB',
          boxShadow: 'none !important',
          backgroundColor: 'transparent !important',
          transition: 'none !important'
        },
        '.Tab:hover': {
          color: '#FF4775',
          border: '1px solid #E5E7EB !important',
          boxShadow: 'none !important',
          backgroundColor: 'transparent !important'
        },
        '.Tab:focus': {
          boxShadow: 'none !important',
          border: '1px solid #FF4775 !important'
        },
        '.Tab--selected': {
          borderColor: '#FF4775 !important',
          boxShadow: 'none !important'
        },
        '.Input': {
          border: '1px solid #E5E7EB !important',
          boxShadow: 'none !important',
          backgroundColor: 'transparent !important',
          padding: '12px !important'
        },
        '.Input:hover': {
          boxShadow: 'none !important',
          border: '1px solid #E5E7EB !important',
          backgroundColor: 'transparent !important'
        },
        '.Input:focus': {
          boxShadow: 'none !important',
          border: '1px solid #FF4775 !important'
        },
        '.Label': {
          fontWeight: '500',
          color: '#374151'
        },
        '.Error': {
          color: '#EF4444',
          fontSize: '14px'
        }
      },
    },
    paymentMethodOrder: ['card'],
    loader: 'auto',
  };

  if (!reviewData) {
    return null;
  }

  // Wrapper function to handle payment processing
  const handlePaymentSubmit = async (paymentMethodId) => {
    try {
      setLocalLoading(true);
      
      // Use the createPayment function passed from parent
      const paymentResult = await createPayment(paymentMethodId);
      
      // Handle success
      if (onPaymentSuccess) {
        onPaymentSuccess(paymentResult);
      }
      
    } catch (error) {
      console.error('Payment submission error:', error);
      // Error is already handled by parent component
    } finally {
      setLocalLoading(false);
    }
  };

  // Order Summary Component
  const OrderSummary = () => (
    <Box sx={{ 
      p: 3, 
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Order Summary
      </Typography>
      
      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {displayName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {carrierName} • {numLines} {numLines === 1 ? 'line' : 'lines'}
        </Typography>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Today's Payment */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Activation Fee:
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            ${activationFee.toFixed(2)}
          </Typography>
        </Box>
        
        {!isEsim && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Shipping:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              ${shippingCost.toFixed(2)}
            </Typography>
          </Box>
        )}
        
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 2,
          p: 2,
          bgcolor: '#FEF2F2',
          borderRadius: 1
        }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Total Due Today:
          </Typography>
          <Typography variant="subtitle2" fontWeight={600}>
            ${(activationFee + shippingCost).toFixed(2)}
          </Typography>
        </Box>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      {/* Monthly Service */}
      <Box>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Monthly Service:
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Monthly Cost:
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            ${monthlyRate.toFixed(2)}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Taxes & Fees:
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            ${taxes.toFixed(2)}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Total Monthly:
          </Typography>
          <Typography variant="subtitle2" fontWeight={600}>
            ${totalMonthly.toFixed(2)}
          </Typography>
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'right' }}>
          Your first bill will arrive after activation
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: '1000px', mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography
        variant="h4"
        className="payment-title"
        align="center"
        gutterBottom
        sx={{
          fontSize: { xs: '24px', md: '28px' },
          fontWeight: 600,
          mb: 1
        }}
      >
        Complete Your Payment
      </Typography>
      
      <Typography
        variant="body1"
        className="payment-subtitle"
        align="center"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Securely pay for your carrier transfer
      </Typography>
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: 1,
        mb: 4,
        mx: 'auto',
        bgcolor: '#ffeaee',
        py: 1,
        px: 2,
        borderRadius: 2,
        width: 'fit-content'
      }}>
        <Lock size={16} color="#ff385c" />
        <Typography variant="body2" sx={{ color: '#ff385c', fontWeight: 500 }}>
          Secure Payment
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        {/* Left column - Payment Form */}
        <Grid item xs={12} md={7}>
          <Paper 
            elevation={0} 
            sx={{ 
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Box sx={{ p: 3 }}>
              <Box 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: '#FEF2F2', 
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2
                }}
              >
                <Box sx={{ color: '#E11D48', mt: '2px' }}>⊙</Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} color="#E11D48">
                    Transfer Subscription
                  </Typography>
                  <Typography variant="body2">
                    You're only paying for the carrier activation fee. Your CircledIn subscription will remain active.
                  </Typography>
                </Box>
              </Box>
              
              <Typography variant="h6" gutterBottom>
                Payment Details
              </Typography>
              
              <Elements stripe={stripePromise} options={options}>
                <TransferPaymentForm
                  reviewData={reviewData}
                  onBack={onBack}
                  onPaymentSuccess={handlePaymentSubmit}
                  setLoading={setLocalLoading}
                  isLoading={loading}
                  hideInfoBox={true} // Hide redundant info box since we already show it above
                />
              </Elements>
            </Box>
          </Paper>
        </Grid>
        
        {/* Right column - Order Summary */}
        <Grid item xs={12} md={5}>
          <Paper 
            elevation={0} 
            sx={{ 
              height: '100%',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <OrderSummary />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TransferPayment;